import React, { useState, useMemo, useEffect } from 'react';
import { Button } from '../../../../../components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogFooter } from '../../../../../components/ui/dialog';
import { Input } from '../../../../../components/ui/input';
import { Switch } from '../../../../../components/ui/switch';
import { Download, FileText, X, Search, CheckCircle, XCircle, Loader2, FileCheck, ChevronDown, ChevronUp, ArrowLeft } from 'lucide-react';
import { Notification } from '../../../../../components/ui/custom-notification';
import { UpdateDocumentApprovalStatus } from '../../../../../redux/Project/specificationSaga';
import { PdfViewer } from '../../../../../components/viewers/PdfViewer';
import { MediaViewer } from '../../../../../components/viewers/MediaViewer';
import { DocumentViewer } from '../../../../../components/viewers/DocumentViewer';

interface DocumentApprovalModalProps {
  isOpen: boolean;
  onClose: () => void;
  productionSheetData: any;
  onOpenViewer?: (fileOrFiles: any | any[]) => void;
  onStatusChange?: (documentId: string, documentType: string, status: 'approved' | 'pending') => void;
  selectedPipe?: any;
  assemblyNumber?: string;
  onDownloadMergedReport?: (assembly: any) => Promise<void>;
  isMergeReport?: boolean;
  onRefresh?: () => void;
  isFitmentreport?: boolean;
  isVisualInspectionreport?: boolean;
  isAlignmentreport?: boolean;
  isProductionSheetreport?: boolean;
  isNDTreport?: boolean;
  isCoatingreport?: boolean;
  isHydroreport?: boolean;
  isTorquingreport?: boolean;
  isMergeProductionReport?: boolean;
  isVissibleApprovalButton?: boolean;
  isWeldingreport?: boolean;
  isWeldReportreport?: boolean;
}

interface DocumentItem {
  id: string;
  docId?: number;
  name: string;
  type: string;
  fileUrl?: string;
  fileName?: string;
  status?: 'approved' | 'pending' | null;
  category: string;
  uploadDate?: string;
  uploadedBy?: string;
  versionNumber?: number;
  isLatest?: boolean;
  weldName?: string;
}

export const DocumentApprovalModal: React.FC<DocumentApprovalModalProps> = ({
  isOpen,
  onClose,
  productionSheetData,
  onOpenViewer,
  onStatusChange,
  selectedPipe,
  assemblyNumber,
  onDownloadMergedReport,
  isMergeReport = false,
  isFitmentreport = false,
  isVisualInspectionreport = false,
  isAlignmentreport = false,
  isProductionSheetreport = false,
  isNDTreport = false,
  isCoatingreport = false,
  isHydroreport = false,
  isTorquingreport = false,
  onRefresh,
  isMergeProductionReport = false,
  isVissibleApprovalButton = false,
  isWeldingreport = false,
  isWeldReportreport = false,
}) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [documentStatuses, setDocumentStatuses] = useState<Record<string, 'approved' | 'pending' | null>>({});
  const [initialStatuses, setInitialStatuses] = useState<Record<string, 'approved' | 'pending' | null>>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [expandedGroups, setExpandedGroups] = useState<Set<string>>(new Set());
  const [selectedDocumentIds, setSelectedDocumentIds] = useState<string[]>([]);

  // Viewer state management
  const [viewerType, setViewerType] = useState<'pdf' | 'media' | 'document' | null>(null);
  const [viewerFile, setViewerFile] = useState<any | null>(null);
  const [viewerFiles, setViewerFiles] = useState<any[]>([]);

  // Collect all documents from the assembly
  const allDocuments = useMemo(() => {
    const documents: DocumentItem[] = [];

    if (!productionSheetData) return documents;

    const productionSheet = productionSheetData;

    // Filter documents based on report type flags
    const includeWeldReport = isWeldReportreport || isWeldingreport;
    const shouldIncludeFitment = isFitmentreport || (!isFitmentreport && !isVisualInspectionreport && !isAlignmentreport && !isProductionSheetreport && !includeWeldReport);
    const shouldIncludeVisual = isVisualInspectionreport || (!isFitmentreport && !isVisualInspectionreport && !isAlignmentreport && !isProductionSheetreport && !includeWeldReport);
    const shouldIncludeAlignment = isAlignmentreport || (!isFitmentreport && !isVisualInspectionreport && !isAlignmentreport && !isProductionSheetreport && !includeWeldReport);
    const shouldIncludeProductionSheet = isProductionSheetreport || (!isFitmentreport && !isVisualInspectionreport && !isAlignmentreport && !isProductionSheetreport && !includeWeldReport);
    const shouldIncludeWeldReport = includeWeldReport || (!isFitmentreport && !isVisualInspectionreport && !isAlignmentreport && !isProductionSheetreport && !includeWeldReport);

    // If isMergeReport is true, only show Merged Production Report documents
    if (isMergeReport) {
      // Merged Production Sheet Report Documents
      if (productionSheet?.mergedProductionSheetReportDocs) {
        const mergedProductionSheetReportDocs = productionSheet.mergedProductionSheetReportDocs;


        //  console.log('mergedProductionSheetReportDocs', mergedProductionSheetReportDocs);
        // Sort by versionNumber descending (latest first)
        const sortedMergedDocs = [...mergedProductionSheetReportDocs].sort((a, b) => {
          const versionA = a.details?.versionNumber || 0;
          const versionB = b.details?.versionNumber || 0;
          return versionB - versionA;
        });

        sortedMergedDocs.forEach((doc: any, index: number) => {
          if (doc && (doc.fileUrl || doc.fileName || doc.file_name)) {
            let status: 'approved' | 'pending' | null = null;
            if (doc.approvalStatus === 'approved') {
              status = 'approved';
            } else if (doc.approvalStatus === 'rejected') {
              status = 'pending';
            }

            documents.push({
              id: `merged-${doc.docId || index}`,
              docId: doc.docId,
              name: doc.fileName || doc.file_name || 'Merged Production Report Document',
              type: 'MERGED PRODUCTION REPORT',
              fileUrl: doc.fileUrl,
              fileName: doc.fileName || doc.file_name,
              category: 'Merged Production Report',
              status: status,
              uploadDate: doc.uploadDate,
              uploadedBy: doc.uploadedBy,
              versionNumber: doc.details?.versionNumber || 0,
              isLatest: index === 0
            });
          }
        });
      }
      return documents;
    }


    // Fitment Documents
    if (shouldIncludeFitment && productionSheet?.fitmentReportDocs) {
      const fitmentDocs = productionSheet.fitmentReportDocs;

      // Group by weldName first, then sort each group
      const weldGroups: Record<string, any[]> = {};
      const noWeldDocs: any[] = [];

      fitmentDocs.forEach((doc: any) => {
        const weldName = doc.details?.weldName;
        if (weldName) {
          if (!weldGroups[weldName]) {
            weldGroups[weldName] = [];
          }
          weldGroups[weldName].push(doc);
        } else {
          noWeldDocs.push(doc);
        }
      });

      // Process each weld group
      Object.entries(weldGroups).forEach(([weldName, weldDocs]) => {
        // Sort by versionNumber descending (latest first) within this weld group
        const sortedWeldDocs = [...weldDocs].sort((a, b) => {
          const versionA = a.details?.versionNumber || 0;
          const versionB = b.details?.versionNumber || 0;
          return versionB - versionA;
        });

        sortedWeldDocs.forEach((doc: any, index: number) => {
          if (doc && (doc.fileUrl || doc.fileName || doc.file_name)) {
            let status: 'approved' | 'pending' | null = null;
            if (doc.approvalStatus === 'approved') {
              status = 'approved';
            } else if (doc.approvalStatus === 'rejected') {
              status = 'pending';
            }

            documents.push({
              id: `fitment-${doc.docId || index}`,
              docId: doc.docId,
              name: doc.fileName || doc.file_name || 'Fitment Readings Document',
              type: 'FITMENT_READINGS',
              fileUrl: doc.fileUrl,
              fileName: doc.fileName || doc.file_name,
              category: 'Fitment',
              status: status,
              uploadDate: doc.uploadDate,
              uploadedBy: doc.uploadedBy,
              versionNumber: doc.details?.versionNumber || 0,
              isLatest: index === 0,
              weldName: weldName
            });
          }
        });
      });

      // Process documents without weldName
      const sortedNoWeldDocs = [...noWeldDocs].sort((a, b) => {
        const versionA = a.details?.versionNumber || 0;
        const versionB = b.details?.versionNumber || 0;
        return versionB - versionA;
      });

      sortedNoWeldDocs.forEach((doc: any, index: number) => {
        if (doc && (doc.fileUrl || doc.fileName || doc.file_name)) {
          let status: 'approved' | 'pending' | null = null;
          if (doc.approvalStatus === 'approved') {
            status = 'approved';
          } else if (doc.approvalStatus === 'rejected') {
            status = 'pending';
          }

          documents.push({
            id: `fitment-${doc.docId || index}`,
            docId: doc.docId,
            name: doc.fileName || doc.file_name || 'Fitment Readings Document',
            type: 'FITMENT_READINGS',
            fileUrl: doc.fileUrl,
            fileName: doc.fileName || doc.file_name,
            category: 'Fitment',
            status: status,
            uploadDate: doc.uploadDate,
            uploadedBy: doc.uploadedBy,
            versionNumber: doc.details?.versionNumber || 0,
            isLatest: index === 0
          });
        }
      });
    }

    // Visual Inspection Documents
    if (shouldIncludeVisual && productionSheet?.visualInspectionReportDocs) {
      const visualDocs = productionSheet.visualInspectionReportDocs;

      // Group by weldName first, then sort each group
      const weldGroups: Record<string, any[]> = {};
      const noWeldDocs: any[] = [];

      visualDocs.forEach((doc: any) => {
        const weldName = doc.details?.weldName;
        if (weldName) {
          if (!weldGroups[weldName]) {
            weldGroups[weldName] = [];
          }
          weldGroups[weldName].push(doc);
        } else {
          noWeldDocs.push(doc);
        }
      });

      // Process each weld group
      Object.entries(weldGroups).forEach(([weldName, weldDocs]) => {
        // Sort by versionNumber descending (latest first) within this weld group
        const sortedWeldDocs = [...weldDocs].sort((a, b) => {
          const versionA = a.details?.versionNumber || 0;
          const versionB = b.details?.versionNumber || 0;
          return versionB - versionA;
        });

        sortedWeldDocs.forEach((doc: any, index: number) => {
          if (doc && (doc.fileUrl || doc.fileName || doc.file_name)) {
            let status: 'approved' | 'pending' | null = null;
            if (doc.approvalStatus === 'approved') {
              status = 'approved';
            } else if (doc.approvalStatus === 'rejected') {
              status = 'pending';
            }

            documents.push({
              id: `visual-${doc.docId || index}`,
              docId: doc.docId,
              name: doc.fileName || doc.file_name || 'Visual Inspection Document',
              type: 'VISUAL_INSPECTION',
              fileUrl: doc.fileUrl,
              fileName: doc.fileName || doc.file_name,
              category: 'Visual Inspection',
              status: status,
              uploadDate: doc.uploadDate,
              uploadedBy: doc.uploadedBy,
              versionNumber: doc.details?.versionNumber || 0,
              isLatest: index === 0,
              weldName: weldName
            });
          }
        });
      });

      // Process documents without weldName
      const sortedNoWeldDocs = [...noWeldDocs].sort((a, b) => {
        const versionA = a.details?.versionNumber || 0;
        const versionB = b.details?.versionNumber || 0;
        return versionB - versionA;
      });

      sortedNoWeldDocs.forEach((doc: any, index: number) => {
        if (doc && (doc.fileUrl || doc.fileName || doc.file_name)) {
          let status: 'approved' | 'pending' | null = null;
          if (doc.approvalStatus === 'approved') {
            status = 'approved';
          } else if (doc.approvalStatus === 'rejected') {
            status = 'pending';
          }

          documents.push({
            id: `visual-${doc.docId || index}`,
            docId: doc.docId,
            name: doc.fileName || doc.file_name || 'Visual Inspection Document',
            type: 'VISUAL_INSPECTION',
            fileUrl: doc.fileUrl,
            fileName: doc.fileName || doc.file_name,
            category: 'Visual Inspection',
            status: status,
            uploadDate: doc.uploadDate,
            uploadedBy: doc.uploadedBy,
            versionNumber: doc.details?.versionNumber || 0,
            isLatest: index === 0
          });
        }
      });
    }
    // Alignment Documents
    if (shouldIncludeAlignment && productionSheet?.alignmentReportDocs) {
      const alignmentDocs = productionSheet.alignmentReportDocs;


      // // Array.isArray(productionSheet.alignmentDoc) 
      //   ? productionSheet.alignmentDoc 
      //   : [productionSheet.alignmentDoc];

      // Sort by versionNumber descending (latest first)
      const sortedAlignmentDocs = [...alignmentDocs].sort((a, b) => {
        const versionA = a.details?.versionNumber || 0;
        const versionB = b.details?.versionNumber || 0;
        return versionB - versionA;
      });

      sortedAlignmentDocs.forEach((doc: any, index: number) => {
        if (doc && (doc.fileUrl || doc.fileName || doc.file_name)) {
          // Map approvalStatus: "approved" -> "accept", "rejected" -> "reject", others -> null
          let status: 'approved' | 'pending' | null = null;
          if (doc.approvalStatus === 'approved') {
            status = 'approved';
          } else if (doc.approvalStatus === 'rejected') {
            status = 'pending';
          }

          documents.push({
            id: `alignment-${doc.docId || index}`,
            docId: doc.docId,
            name: doc.fileName || doc.file_name || 'Alignment Document',
            type: 'ALIGNMENT',
            fileUrl: doc.fileUrl,
            fileName: doc.fileName || doc.file_name,
            category: 'Alignment',
            status: status,
            uploadDate: doc.uploadDate,
            uploadedBy: doc.uploadedBy,
            versionNumber: doc.details?.versionNumber || 0,
            isLatest: index === 0
          });
        }
      });
    }

    // Production Sheet Main Report Documents
    if (shouldIncludeProductionSheet && productionSheet?.productionSheetMainReportDocs) {
      const mainReportDocs = productionSheet.productionSheetMainReportDocs;

      // Sort by versionNumber descending (latest first)
      const sortedMainReportDocs = [...mainReportDocs].sort((a, b) => {
        const versionA = a.details?.versionNumber || 0;
        const versionB = b.details?.versionNumber || 0;
        return versionB - versionA;
      });

      sortedMainReportDocs.forEach((doc: any, index: number) => {
        if (doc && (doc.fileUrl || doc.fileName || doc.file_name)) {
          let status: 'approved' | 'pending' | null = null;
          if (doc.approvalStatus === 'approved') {
            status = 'approved';
          } else if (doc.approvalStatus === 'rejected') {
            status = 'pending';
          }

          documents.push({
            id: `production-main-${doc.docId || index}`,
            docId: doc.docId,
            name: doc.fileName || doc.file_name || 'Production Report Document',
            type: 'PRODUCTION REPORT',
            fileUrl: doc.fileUrl,
            fileName: doc.fileName || doc.file_name,
            category: 'Production Report',
            status: status,
            uploadDate: doc.uploadDate,
            uploadedBy: doc.uploadedBy,
            versionNumber: doc.details?.versionNumber || 0,
            isLatest: index === 0
          });
        }
      });
    }

    // Weld Report Documents (generated welder signature / weld reports)
    if (shouldIncludeWeldReport && productionSheet?.weldReportDocs) {
      const weldReportDocs = productionSheet.weldReportDocs;

      // Sort by versionNumber descending (latest first)
      const sortedWeldReportDocs = [...weldReportDocs].sort((a, b) => {
        const versionA = a.details?.versionNumber || 0;
        const versionB = b.details?.versionNumber || 0;
        return versionB - versionA;
      });

      sortedWeldReportDocs.forEach((doc: any, index: number) => {
        if (doc && (doc.fileUrl || doc.fileName || doc.file_name)) {
          let status: 'approved' | 'pending' | null = null;
          if (doc.approvalStatus === 'approved') {
            status = 'approved';
          } else if (doc.approvalStatus === 'rejected') {
            status = 'pending';
          }

          documents.push({
            id: `weld-report-${doc.docId || index}`,
            docId: doc.docId,
            name: doc.fileName || doc.file_name || 'Weld Report Document',
            type: 'WELD REPORT',
            fileUrl: doc.fileUrl,
            fileName: doc.fileName || doc.file_name,
            category: 'Weld Report',
            status: status,
            uploadDate: doc.uploadDate,
            uploadedBy: doc.uploadedBy,
            versionNumber: doc.details?.versionNumber || 0,
            isLatest: index === 0
          });
        }
      });
    }

    // Welding Documents (uploaded from Assembly table Welding column → productionSheet.weldingDoc)
    if (isWeldingreport && productionSheet?.weldingDoc && Array.isArray(productionSheet.weldingDoc)) {
      const weldingDocs = productionSheet.weldingDoc;

      const sortedWeldingDocs = [...weldingDocs].sort((a, b) => {
        const versionA = a.docId || 0;
        const versionB = b.docId || 0;
        return versionB - versionA;
      });

      sortedWeldingDocs.forEach((doc: any, index: number) => {
        if (doc && (doc.fileUrl || doc.fileName || doc.file_name)) {
          let status: 'approved' | 'pending' | null = null;
          if (doc.approvalStatus === 'approved') {
            status = 'approved';
          } else if (doc.approvalStatus === 'rejected') {
            status = 'pending';
          }

          documents.push({
            id: `welding-${doc.docId || index}`,
            docId: doc.docId,
            name: doc.fileName || doc.file_name || 'Welding Document',
            type: 'WELDING',
            fileUrl: doc.fileUrl,
            fileName: doc.fileName || doc.file_name,
            category: 'Welding',
            status: status,
            uploadDate: doc.uploadDate,
            uploadedBy: doc.uploadedBy,
            versionNumber: index + 1,
            isLatest: index === 0
          });
        }
      });
    }

    // NDT Documents
    if (isNDTreport && productionSheet?.ndtDoc && Array.isArray(productionSheet.ndtDoc)) {
      const ndtDocs = productionSheet.ndtDoc;

      // Sort by versionNumber descending (latest first)
      const sortedNdtDocs = [...ndtDocs].sort((a, b) => {
        const versionA = a.docId || 0;
        const versionB = b.docId || 0;
        return versionB - versionA;
      });

      sortedNdtDocs.forEach((doc: any, index: number) => {
        if (doc && (doc.fileUrl || doc.fileName || doc.file_name)) {
          let status: 'approved' | 'pending' | null = null;
          if (doc.approvalStatus === 'approved') {
            status = 'approved';
          } else if (doc.approvalStatus === 'rejected') {
            status = 'pending';
          }

          documents.push({
            id: `ndt-${doc.docId || index}`,
            docId: doc.docId,
            name: doc.fileName || doc.file_name || 'NDT Document',
            type: 'NDT',
            fileUrl: doc.fileUrl,
            fileName: doc.fileName || doc.file_name,
            category: 'NDT',
            status: status,
            uploadDate: doc.uploadDate,
            uploadedBy: doc.uploadedBy,
            versionNumber: index + 1,
            isLatest: index === 0
          });
        }
      });
    }

    // Coating Documents
    if (isCoatingreport && productionSheet?.coatingDoc && Array.isArray(productionSheet.coatingDoc)) {
      const coatingDocs = productionSheet.coatingDoc;

      // Sort by versionNumber descending (latest first)
      const sortedCoatingDocs = [...coatingDocs].sort((a, b) => {
        const versionA = a.docId || 0;
        const versionB = b.docId || 0;
        return versionB - versionA;
      });

      sortedCoatingDocs.forEach((doc: any, index: number) => {
        if (doc && (doc.fileUrl || doc.fileName || doc.file_name)) {
          let status: 'approved' | 'pending' | null = null;
          if (doc.approvalStatus === 'approved') {
            status = 'approved';
          } else if (doc.approvalStatus === 'rejected') {
            status = 'pending';
          }

          documents.push({
            id: `coating-${doc.docId || index}`,
            docId: doc.docId,
            name: doc.fileName || doc.file_name || 'Coating Document',
            type: 'COATING',
            fileUrl: doc.fileUrl,
            fileName: doc.fileName || doc.file_name,
            category: 'Coating',
            status: status,
            uploadDate: doc.uploadDate,
            uploadedBy: doc.uploadedBy,
            versionNumber: index + 1,
            isLatest: index === 0
          });
        }
      });
    }

    // Hydro Test Report Documents
    if (isHydroreport && productionSheet?.hydroDoc && Array.isArray(productionSheet.hydroDoc)) {
      const hydroDocs = productionSheet.hydroDoc;

      const sortedHydroDocs = [...hydroDocs].sort((a, b) => {
        const versionA = a.docId || 0;
        const versionB = b.docId || 0;
        return versionB - versionA;
      });

      sortedHydroDocs.forEach((doc: any, index: number) => {
        if (doc && (doc.fileUrl || doc.fileName || doc.file_name)) {
          let status: 'approved' | 'pending' | null = null;
          if (doc.approvalStatus === 'approved') {
            status = 'approved';
          } else if (doc.approvalStatus === 'rejected') {
            status = 'pending';
          }

          documents.push({
            id: `hydro-${doc.docId || index}`,
            docId: doc.docId,
            name: doc.fileName || doc.file_name || 'Hydro Test Report',
            type: 'HYDRO',
            fileUrl: doc.fileUrl,
            fileName: doc.fileName || doc.file_name,
            category: 'Hydro',
            status: status,
            uploadDate: doc.uploadDate,
            uploadedBy: doc.uploadedBy,
            versionNumber: index + 1,
            isLatest: index === 0
          });
        }
      });
    }

    // Torquing Report Documents
    if (isTorquingreport && productionSheet?.torquingDoc && Array.isArray(productionSheet.torquingDoc)) {
      const torquingDocs = productionSheet.torquingDoc;

      const sortedTorquingDocs = [...torquingDocs].sort((a, b) => {
        const versionA = a.docId || 0;
        const versionB = b.docId || 0;
        return versionB - versionA;
      });

      sortedTorquingDocs.forEach((doc: any, index: number) => {
        if (doc && (doc.fileUrl || doc.fileName || doc.file_name)) {
          let status: 'approved' | 'pending' | null = null;
          if (doc.approvalStatus === 'approved') {
            status = 'approved';
          } else if (doc.approvalStatus === 'rejected') {
            status = 'pending';
          }

          documents.push({
            id: `torquing-${doc.docId || index}`,
            docId: doc.docId,
            name: doc.fileName || doc.file_name || 'Torquing Report',
            type: 'TORQUING',
            fileUrl: doc.fileUrl,
            fileName: doc.fileName || doc.file_name,
            category: 'Torquing',
            status: status,
            uploadDate: doc.uploadDate,
            uploadedBy: doc.uploadedBy,
            versionNumber: index + 1,
            isLatest: index === 0
          });
        }
      });
    }



    //  // Merged Production Sheet Report Documents
    //     if (isMergeReport && productionSheet?.mergedProductionSheetReportDocs) {
    //       const mergedProductionSheetReportDocs = productionSheet.mergedProductionSheetReportDocs;

    //       // Sort by versionNumber descending (latest first)
    //       const sortedMergedDocs = [...mergedProductionSheetReportDocs].sort((a, b) => {
    //         const versionA = a.details?.versionNumber || 0;
    //         const versionB = b.details?.versionNumber || 0;
    //         return versionB - versionA;
    //       });

    //       sortedMergedDocs.forEach((doc: any, index: number) => {
    //         if (doc && (doc.fileUrl || doc.fileName || doc.file_name)) {
    //           let status: 'approved' | 'pending' | null = null;
    //           if (doc.approvalStatus === 'approved') {
    //             status = 'approved';
    //           } else if (doc.approvalStatus === 'rejected') {
    //             status = 'pending';
    //           }

    //           documents.push({
    //             id: `merged-${doc.docId || index}`,
    //             docId: doc.docId,
    //             name: doc.fileName || doc.file_name || 'Merged Production Report Document',
    //             type: 'MERGED PRODUCTION REPORT',
    //             fileUrl: doc.fileUrl,
    //             fileName: doc.fileName || doc.file_name,
    //             category: 'Merged Production Report',
    //             status: status,
    //             uploadDate: doc.uploadDate,
    //             uploadedBy: doc.uploadedBy,
    //             versionNumber: doc.details?.versionNumber || 0,
    //             isLatest: index === 0
    //           });
    //         }
    //       });
    //     }



    // // Generator Report Document
    // if (extraDetails?.generatorReportDoc) {
    //   const generatorDocs = Array.isArray(extraDetails.generatorReportDoc)
    //     ? extraDetails.generatorReportDoc
    //     : [extraDetails.generatorReportDoc];

    //   generatorDocs.forEach((doc: any, index: number) => {
    //     if (doc && (doc.fileUrl || doc.fileName || doc.file_name)) {
    //       let status: 'approved' | 'pending' | null = null;
    //       if (doc.approvalStatus === 'approved') {
    //         status = 'approved';
    //       } else if (doc.approvalStatus === 'rejected') {
    //         status = 'pending';
    //       }

    //       documents.push({
    //         id: `generator-${doc.docId || index}`,
    //         docId: doc.docId,
    //         name: doc.fileName || doc.file_name || 'Generator Report Document',
    //         type: 'GENERATOR_REPORT',
    //         fileUrl: doc.fileUrl,
    //         fileName: doc.fileName || doc.file_name,
    //         category: 'Generator Report',
    //         status: status
    //       });
    //     }
    //   });
    // }

    // // Inspection Procedure Document
    // if (extraDetails?.inspectionProcedureDoc) {
    //   const inspectionDocs = Array.isArray(extraDetails.inspectionProcedureDoc)
    //     ? extraDetails.inspectionProcedureDoc
    //     : [extraDetails.inspectionProcedureDoc];

    //   inspectionDocs.forEach((doc: any, index: number) => {
    //     if (doc && (doc.fileUrl || doc.fileName || doc.file_name)) {
    //       let status: 'approved' | 'pending' | null = null;
    //       if (doc.approvalStatus === 'approved') {
    //         status = 'approved';
    //       } else if (doc.approvalStatus === 'rejected') {
    //         status = 'pending';
    //       }

    //       documents.push({
    //         id: `inspection-${doc.docId || index}`,
    //         docId: doc.docId,
    //         name: doc.fileName || doc.file_name || 'Inspection Procedure Document',
    //         type: 'INSPECTION_PROCEDURE',
    //         fileUrl: doc.fileUrl,
    //         fileName: doc.fileName || doc.file_name,
    //         category: 'Inspection Procedure',
    //         status: status
    //       });
    //     }
    //   });
    // }

    // // Report Document
    // if (extraDetails?.reportDoc) {
    //   if (extraDetails.reportDoc.fileUrl || extraDetails.reportDoc.fileName) {
    //     let status: 'approved' | 'pending' | null = null;
    //     if (extraDetails.reportDoc.approvalStatus === 'approved') {
    //       status = 'approved';
    //     } else if (extraDetails.reportDoc.approvalStatus === 'rejected') {
    //       status = 'pending';
    //     }

    //     documents.push({
    //       id: `report-${extraDetails.reportDoc.docId || 'report'}`,
    //       docId: extraDetails.reportDoc.docId,
    //       name: extraDetails.reportDoc.fileName || 'Production Report',
    //       type: 'REPORT',
    //       fileUrl: extraDetails.reportDoc.fileUrl,
    //       fileName: extraDetails.reportDoc.fileName,
    //       category: 'Report',
    //       status: status
    //     });
    //   }
    // }

    // // Reporting Document (legacy)
    // if (extraDetails?.reportingDoc) {
    //   if (extraDetails.reportingDoc.fileUrl || extraDetails.reportingDoc.fileName) {
    //     let status: 'approved' | 'pending' | null = null;
    //     if (extraDetails.reportingDoc.approvalStatus === 'approved') {
    //       status = 'approved';
    //     } else if (extraDetails.reportingDoc.approvalStatus === 'rejected') {
    //       status = 'pending';
    //     }

    //     documents.push({
    //       id: `reporting-${extraDetails.reportingDoc.docId || 'reporting'}`,
    //       docId: extraDetails.reportingDoc.docId,
    //       name: extraDetails.reportingDoc.fileName || 'Reporting Document',
    //       type: 'REPORTING',
    //       fileUrl: extraDetails.reportingDoc.fileUrl,
    //       fileName: extraDetails.reportingDoc.fileName,
    //       category: 'Reporting',
    //       status: status
    //     });
    //   }
    // }

    // // NDT Documents
    // if (productionSheet?.ndtDoc && Array.isArray(productionSheet.ndtDoc)) {
    //   productionSheet.ndtDoc.forEach((doc: any, index: number) => {
    //     if (doc && (doc.fileUrl || doc.fileName || doc.file_name)) {
    //       let status: 'approved' | 'pending' | null = null;
    //       if (doc.approvalStatus === 'approved') {
    //         status = 'approved';
    //       } else if (doc.approvalStatus === 'rejected') {
    //         status = 'pending';
    //       }

    //       documents.push({
    //         id: `ndt-${doc.docId || index}`,
    //         docId: doc.docId,
    //         name: doc.fileName || doc.file_name || 'NDT Document',
    //         type: 'NDT',
    //         fileUrl: doc.fileUrl,
    //         fileName: doc.fileName || doc.file_name,
    //         category: 'NDT',
    //         status: status
    //       });
    //     }
    //   });
    // }

    // // Coating Documents
    // if (productionSheet?.coatingDoc && Array.isArray(productionSheet.coatingDoc)) {
    //   productionSheet.coatingDoc.forEach((doc: any, index: number) => {
    //     if (doc && (doc.fileUrl || doc.fileName || doc.file_name)) {
    //         let status: 'approved' | 'pending' | null = null;
    //       if (doc.approvalStatus === 'approved') {
    //         status = 'approved';
    //       } else if (doc.approvalStatus === 'rejected') {
    //         status = 'pending';
    //       }

    //       documents.push({
    //         id: `coating-${doc.docId || index}`,
    //         docId: doc.docId,
    //         name: doc.fileName || doc.file_name || 'Coating Document',
    //         type: 'COATING',
    //         fileUrl: doc.fileUrl,
    //         fileName: doc.fileName || doc.file_name,
    //         category: 'Coating',
    //         status: status
    //       });
    //     }
    //   });
    // }

    // // WPS Document
    // if (productionSheet?.wpsDoc) {
    //   if (productionSheet.wpsDoc.fileUrl || productionSheet.wpsDoc.fileName) {
    //     let status: 'approved' | 'pending' | null = null;
    //     if (productionSheet.wpsDoc.approvalStatus === 'approved') {
    //       status = 'approved';
    //     } else if (productionSheet.wpsDoc.approvalStatus === 'rejected') {
    //       status = 'pending';
    //     }

    //     documents.push({
    //       id: `wps-${productionSheet.wpsDoc.docId || 'wps'}`,
    //       docId: productionSheet.wpsDoc.docId,
    //       name: productionSheet.wpsDoc.fileName || 'WPS Document',
    //       type: 'WPS',
    //       fileUrl: productionSheet.wpsDoc.fileUrl,
    //       fileName: productionSheet.wpsDoc.fileName,
    //       category: 'WPS',
    //       status: status
    //     });
    //   }
    // }

    // // Drawing Document
    // if (productionSheet?.drawingDoc) {
    //   if (productionSheet.drawingDoc.fileUrl || productionSheet.drawingDoc.fileName) {
    //     let status: 'approved' | 'pending' | null = null;
    //     if (productionSheet.drawingDoc.approvalStatus === 'approved') {
    //       status = 'approved';
    //     } else if (productionSheet.drawingDoc.approvalStatus === 'rejected') {
    //       status = 'pending';
    //     }

    //     documents.push({
    //       id: `drawing-${productionSheet.drawingDoc.docId || 'drawing'}`,
    //       docId: productionSheet.drawingDoc.docId,
    //       name: productionSheet.drawingDoc.fileName || 'Drawing Document',
    //       type: 'DRAWING',
    //       fileUrl: productionSheet.drawingDoc.fileUrl,
    //       fileName: productionSheet.drawingDoc.fileName,
    //       category: 'Drawing',
    //       status: status
    //     });
    //   }
    // }

    return documents;
  }, [productionSheetData, isMergeReport, isFitmentreport, isVisualInspectionreport, isAlignmentreport, isProductionSheetreport, isNDTreport, isCoatingreport, isHydroreport, isTorquingreport, isWeldReportreport, isWeldingreport]);



  const getfitmentWelder1Documents = () => {
    if (productionSheetData?.fitmentReportDocs) {
      return productionSheetData.fitmentReportDocs.filter((doc: any) => (doc.welder1 === true));
    }
    return [];
  }

  const getfitmentWelder2Documents = () => {
    if (productionSheetData?.fitmentReportDocs) {
      return productionSheetData.fitmentReportDocs.filter((doc: any) => (doc.welder2 === true));
    }
    return [];
  }

  // Initialize document statuses from document data when modal opens
  useEffect(() => {
  //  console.log('allDocuments', productionSheetData);
    if (isOpen && allDocuments.length > 0) {



      const initialStatusesMap: Record<string, 'approved' | 'pending' | null> = {};
      allDocuments.forEach(doc => {
        if (doc.status !== undefined) {
          initialStatusesMap[doc.id] = doc.status;
        }
      });
      setInitialStatuses(initialStatusesMap);
      setDocumentStatuses(initialStatusesMap);
      setSelectedDocumentIds([]); // Reset modified documents when modal opens
    } else if (!isOpen) {
      // Reset when modal closes
      setInitialStatuses({});
      setDocumentStatuses({});
      setSelectedDocumentIds([]);
    }
  }, [isOpen, allDocuments]);

  // Check if any changes have been made
  const hasChanges = useMemo(() => {
    // Check if any document status has changed from initial state
    for (const docId in documentStatuses) {
      const currentStatus = documentStatuses[docId];
      const initialStatus = initialStatuses[docId] ?? null;
      if (currentStatus !== initialStatus) {
        return true;
      }
    }
    // Also check if any document in initialStatuses is missing from documentStatuses
    for (const docId in initialStatuses) {
      if (!(docId in documentStatuses)) {
        return true;
      }
    }
    return false;
  }, [documentStatuses, initialStatuses]);

  // Filter documents based on search query
  const filteredDocuments = useMemo(() => {
    if (!searchQuery.trim()) return allDocuments;

    const query = searchQuery.toLowerCase();
    return allDocuments.filter(doc =>
      doc.name.toLowerCase().includes(query) ||
      doc.category.toLowerCase().includes(query) ||
      doc.type.toLowerCase().includes(query)
    );
  }, [allDocuments, searchQuery]);

  // Group documents by category and weldName (if available), separating latest from older versions
  const groupedDocuments = useMemo(() => {
    const groups: Record<string, { latest: DocumentItem | null; older: DocumentItem[] }> = {};

    filteredDocuments.forEach(doc => {
      // For Fitment and Visual Inspection, group by weldName if available
      let groupKey = doc.category;
      if ((doc.category === 'Fitment' || doc.category === 'Visual Inspection') && doc.weldName) {
        groupKey = `${doc.category} - ${doc.weldName}`;
      }

      if (!groups[groupKey]) {
        groups[groupKey] = { latest: null, older: [] };
      }
      if (doc.isLatest) {
        groups[groupKey].latest = doc;
      } else {
        groups[groupKey].older.push(doc);
      }
    });
    return groups;
  }, [filteredDocuments]);

  const toggleGroupExpansion = (category: string) => {
    setExpandedGroups(prev => {
      const newSet = new Set(prev);
      if (newSet.has(category)) {
        newSet.delete(category);
      } else {
        newSet.add(category);
      }
      return newSet;
    });
  };

  // Calculate approved and pending counts
  const statusCounts = useMemo(() => {
    let approved = 0;
    let pending = 0;

    allDocuments.forEach(doc => {
      const status = documentStatuses[doc.id] ?? doc.status ?? null;
      if (status === 'approved') {
        approved++;
      } else {
        pending++;
      }
    });

    return { approved, pending, total: allDocuments.length };
  }, [allDocuments, documentStatuses]);

  // File type detection utilities
  const getFileExtension = (fileName?: string, fileUrl?: string) => {
    const name = fileName || fileUrl?.split('?')[0].split('/').pop() || '';
    return name.split('.').pop()?.toLowerCase() || '';
  };

  const getViewerType = (file: any): 'pdf' | 'media' | 'document' => {
    const ext = getFileExtension(file?.fileName, file?.fileUrl);
    if (ext === 'pdf') return 'pdf';
    const imageExts = ['jpg', 'jpeg', 'png', 'gif', 'svg', 'webp', 'bmp', 'tiff', 'tif'];
    const videoExts = ['mp4', 'avi', 'mov', 'wmv', 'webm'];
    if (imageExts.includes(ext) || videoExts.includes(ext)) return 'media';
    return 'document'; // Default for office docs and fallback
  };

  // Unified viewer handler
  const openViewer = (fileOrFiles: any | any[]) => {
    if (!fileOrFiles) return;

    // Handle array of files
    if (Array.isArray(fileOrFiles)) {
      const validFiles = fileOrFiles.filter((f: any) => f?.fileUrl || f?.file_url);
      if (validFiles.length === 0) {
        Notification.error('No valid files to view');
        return;
      }

      // Check if all files are media (images/videos)
      const allMedia = validFiles.every((file: any) => getViewerType(file) === 'media');
      
      if (allMedia && validFiles.length > 1) {
        // Multiple media files - use MediaViewer
        setViewerFiles(validFiles);
        setViewerType('media');
        setViewerFile(null);
      } else {
        // Single file or mixed types - open first file
        openViewer(validFiles[0]);
      }
      return;
    }

    // Handle single file
    const file = fileOrFiles;
    if (!file?.fileUrl && !file?.file_url) {
      Notification.error('File URL not available');
      return;
    }

    // Normalize file object to ensure fileUrl exists
    const normalizedFile = {
      ...file,
      fileUrl: file.fileUrl || file.file_url || file.url,
      fileName: file.fileName || file.file_name || file.name
    };

    const type = getViewerType(normalizedFile);
    setViewerType(type);
    
    if (type === 'media') {
      // For media, use array format for MediaViewer
      setViewerFiles([normalizedFile]);
      setViewerFile(null);
    } else {
      // For PDF and documents, use single file
      setViewerFile(normalizedFile);
      setViewerFiles([]);
    }
  };

  const closeViewer = () => {
    setViewerType(null);
    setViewerFile(null);
    setViewerFiles([]);
  };

// Opens the viewer for a document.
// If parent provides onOpenViewer, use that and close this modal.
// Otherwise use internal viewer.
const handleDownload = (doc: DocumentItem) => {
  if (!doc || !(doc.fileUrl || (doc as any).file_url || (doc as any).url)) {
    Notification.error('File URL not available');
    return;
  }

  // If parent provides onOpenViewer, use it and close this modal
  if (typeof onOpenViewer === 'function') {
    onOpenViewer(doc);
    onClose();
    return;
  }

  // Fallback: use internal viewer
  openViewer(doc);
};


  const handleStatusToggle = (doc: DocumentItem) => {
    const currentStatus = documentStatuses[doc.id] ?? null;
    const modifiedIds = new Set<string>([doc.id]);

    // Check if this is NDT, Coating, or Hydro (allow multiple approvals)
    const isNDTOrCoating = doc.category === 'NDT' || doc.category === 'Coating' || doc.category === 'Hydro' || doc.category === 'Torquing' || doc.category === 'Welding';

    // Check if this is Fitment or Visual Inspection with weldName (group by weld)
    const isFitmentOrVisualWithWeld = (doc.category === 'Fitment' || doc.category === 'Visual Inspection') && doc.weldName;

    // If already approved, toggle to pending (null)
    if (currentStatus === 'approved') {
      setDocumentStatuses(prev => ({ ...prev, [doc.id]: null }));
      setSelectedDocumentIds(prev => {
        const newSet = new Set(prev);
        newSet.add(doc.id);
        return Array.from(newSet);
      });
      return;
    }

    // If pending, approve this document
    const newStatuses: Record<string, 'approved' | 'pending' | null> = { ...documentStatuses };

    // For NDT and Coating: Allow multiple approvals, don't set others to pending
    // For Fitment/Visual with weldName: Only toggle within the same weld group
    // For other categories: Set all others in the same category to pending
    if (!isNDTOrCoating) {
      // Set documents to pending (null) and track them as modified
      allDocuments.forEach(otherDoc => {
        let shouldToggle = false;

        if (isFitmentOrVisualWithWeld) {
          // For weld groups: only toggle documents with same category AND same weldName
          shouldToggle = otherDoc.category === doc.category &&
            otherDoc.weldName === doc.weldName &&
            otherDoc.id !== doc.id;
        } else {
          // For other categories: toggle all documents in the same category
          shouldToggle = otherDoc.category === doc.category && otherDoc.id !== doc.id;
        }

        if (shouldToggle) {
          const otherDocInitialStatus = initialStatuses[otherDoc.id] ?? null;
          const otherDocCurrentStatus = newStatuses[otherDoc.id] ?? null;

          // Only set to null if it was previously approved (modified)
          if (otherDocCurrentStatus === 'approved' || otherDocInitialStatus === 'approved') {
            newStatuses[otherDoc.id] = null;
            modifiedIds.add(otherDoc.id);
          }
        }
      });
    }

    // Set current document to approved
    newStatuses[doc.id] = 'approved';

    setDocumentStatuses(newStatuses);

    // Track all modified document IDs
    setSelectedDocumentIds(prev => {
      const newSet = new Set(prev);
      modifiedIds.forEach(id => newSet.add(id));
      return Array.from(newSet);
    });
  };

  const formatDate = (dateString: string) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    const day = date.getDate().toString().padStart(2, '0');
    const month = date.toLocaleString('en-US', { month: 'short' });
    const year = date.getFullYear();
    return `${day}-${month}-${year}`;
  };

  const handleSubmit = async () => {
    try {
      setIsSubmitting(true);

      // Build payload array with only modified documents
      // Only include documents that were actually toggled/modified by the user
      const payload = allDocuments
        .filter(doc => {
          // Only include documents with valid docId
          if (!doc.docId) return false;

          // Only include documents that were modified (in selectedDocumentIds)
          if (!selectedDocumentIds.includes(doc.id)) {
            return false;
          }

          // Get current status
          const currentStatus = documentStatuses[doc.id] ?? null;

          // Include documents that are approved OR were changed to pending
          // (if it was approved initially and now is pending, or vice versa)
          return currentStatus === 'approved' || currentStatus === null;
        })
        .map(doc => {
          const currentStatus = documentStatuses[doc.id] ?? null;
          return {
            docId: doc.docId,
            approvalStatus: currentStatus === 'approved' ? 'approved' as const : 'pending' as const
          };
        });

      if (!isMergeReport && productionSheetData?.mergedProductionSheetReportDocs) {
        const mergedProductionSheetReportDocs = productionSheetData.mergedProductionSheetReportDocs.filter((doc: any) => (doc.approvalStatus === 'approved'));
        if (mergedProductionSheetReportDocs.length > 0) {
          payload.push(...mergedProductionSheetReportDocs.map((doc: any) => ({
            docId: doc.docId,
            approvalStatus: 'pending' as const
          })));
        }
      }
      // Only submit if there are modified documents
      if (payload.length === 0) {
        Notification.error('No modified documents found. Please make changes to at least one document.');
        setIsSubmitting(false);
        return;
      }

      // Call API with payload array
      const response = await UpdateDocumentApprovalStatus(payload);

      if (response) {
        //  Notification.success('Document approval status updated successfully');

        // Trigger merged report download after successful approval
        if (!isMergeReport && onDownloadMergedReport && productionSheetData) {
          try {
            await onDownloadMergedReport(productionSheetData);
          } catch (error) {
            // Error handling is done in the download function, just log here
            console.error('Error downloading merged report after approval:', error);
          }
        }

        onClose();
      }
    } catch (error: any) {
      Notification.error(error?.message || 'Failed to update document approval status');
    } finally {
      onRefresh?.();
      setIsSubmitting(false);
    }
  };
  const getTitle = () => {
    return `${assemblyNumber} -${isProductionSheetreport ? 'Production Sheet' : isFitmentreport ? 'Fitment' : isVisualInspectionreport ? 'Visual Inspection' : isAlignmentreport ? 'Alignment' : 'Report'}`;
  };  

  // Check if any viewer is active
  const isViewerOpen = viewerType !== null && (viewerFile !== null || viewerFiles.length > 0);

  // If viewer is open, render ONLY the viewer (not the Dialog) to avoid focus trap issues
  if (isViewerOpen) {
    return (
      <>
        {/* PDF Viewer */}
        {viewerType === 'pdf' && viewerFile && (
          <PdfViewer
            fileUrl={viewerFile.fileUrl}
            fileName={viewerFile.fileName}
            onClose={closeViewer}
          />
        )}

        {/* Media Viewer */}
        {viewerType === 'media' && viewerFiles.length > 0 && (
          <MediaViewer
            files={viewerFiles}
            initialIndex={0}
            onClose={closeViewer}
          />
        )}

        {/* Document Viewer */}
        {viewerType === 'document' && viewerFile && (
          <DocumentViewer
            document={viewerFile}
            onClose={closeViewer}
          />
        )}
      </>
    );
  }

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="w-[90vw] max-w-[1400px] max-h-[90vh] flex flex-col">
        <DialogHeader>
          <div className="flex items-center gap-4">
            <Button
              onClick={onClose}
              className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
              variant="outline"
            >
              <ArrowLeft className="h-5 w-5 text-gray-500" />
            </Button>
            <div>
              <h2 className="text-xl font-semibold">{getTitle() || 'Document Approval'}</h2>
              {selectedPipe && (
                <p className="text-sm text-gray-500">
                  {selectedPipe?.size}" × {selectedPipe?.wallThickness}" Buckle Arrestor
                </p>
              )}
            </div>
          </div>
        </DialogHeader>


        {allDocuments.length === 0 ? (
          <div className="text-center py-8 text-gray-500 space-y-2 border-t">
            <p>No documents available</p>
            <p>Please add signature and save the sheet to view the report documents</p>
          </div>
        ) : 
          <>
          

            {/* Documents List */}
            <div className="flex-1 overflow-y-auto py-4">
            
                <div className="space-y-6">
                  {Object.entries(groupedDocuments).map(([category, { latest, older }]) => {
                    const hasMultipleVersions = older.length > 0;
                    const isExpanded = expandedGroups.has(category);
                    const totalCount = (latest ? 1 : 0) + older.length;

                    // Check if this is NDT, Coating, or Hydro category (no version expand/collapse)
                    const isNDTOrCoating = category === 'NDT' || category === 'Coating' || category === 'Hydro' || category === 'Torquing' || category === 'Welding';
                    const allDocs = latest ? [latest, ...older] : older;

                    // Format header: for weld groups, show "FITMENT - Weld-001 (2)" format
                    const displayHeader = category.includes(' - ')
                      ? category.toUpperCase()
                      : category.toUpperCase();

                    return (
                      <div key={category} className="space-y-2">
                        <h3 className="text-sm font-semibold text-gray-700 uppercase tracking-wide pb-2 border-b">
                          {displayHeader} ({totalCount})
                        </h3>
                        <div className="space-y-2">
                          {/* For NDT and Coating: Show all documents without version expand/collapse */}
                          {isNDTOrCoating ? (
                            allDocs.map((doc) => {
                              const currentStatus = documentStatuses[doc.id] ?? null;
                              return (
                                <div
                                  key={doc.id}
                                  className="flex items-center justify-between p-4 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors border border-gray-200"
                                >
                                  <div className="flex items-center gap-3 flex-1 min-w-0">
                                    <FileText className="h-5 w-5 text-gray-500 flex-shrink-0" />
                                    <div className="flex-1 min-w-0">
                                      <p className="text-sm font-medium text-gray-900 truncate">
                                        {doc.name}
                                      </p>
                                      <div className="flex items-center gap-4 text-xs text-gray-500 mt-1">
                                        <span className="capitalize">{formatDate(doc?.uploadDate || '')}</span>
                                        <span className="capitalize">{doc?.uploadedBy || ''}</span>
                                      </div>
                                    </div>
                                  </div>

                                  <div className="flex items-center gap-3 flex-shrink-0">
                                    {/* Download Button */}
                                    <Button
                                      variant="outline"
                                      size="sm"
                                      onClick={() => handleDownload(doc)}
                                      className="flex items-center gap-2"
                                      disabled={!doc.fileUrl}
                                    >
                                      <Download className="h-4 w-4" />
                                      Download
                                    </Button>

                                    {/* Approve/Pending Toggle Button */}
                                    {isVissibleApprovalButton && (
                                      <Button
                                      variant="outline"
                                      size="sm"
                                      title="Click to Approve report"
                                      onClick={() => handleStatusToggle(doc)}
                                      className={`flex items-center gap-1 ${currentStatus === 'approved'
                                        ? 'bg-green-600 hover:bg-green-700 text-white border-green-600'
                                        : 'bg-gray-100 hover:bg-gray-200 text-gray-700 border-gray-300'
                                        }`}
                                    >
                                      {currentStatus === 'approved' ? (
                                        <>
                                          <CheckCircle className="h-4 w-4" />
                                          Approved
                                        </>
                                      ) : (
                                        <>
                                          <FileCheck className="h-4 w-4" />
                                         
                                          Approve
                                        </>
                                      )}
                                      </Button>
                                    )}
                                  </div>
                                </div>
                              );
                            })
                          ) : (
                            /* Latest Version for other categories */
                            latest && (
                              <div className="space-y-2">
                                <div
                                  className={`flex items-center justify-between p-4 rounded-lg transition-colors border ${latest.isLatest
                                    ? 'bg-blue-50 hover:bg-blue-100'
                                    : 'bg-gray-50 hover:bg-gray-100 border-gray-200'
                                    }`}
                                >
                                  <div className="flex items-center gap-3 flex-1 min-w-0">
                                    <FileText className="h-5 w-5 text-gray-500 flex-shrink-0" />
                                    <div className="flex-1 min-w-0">
                                      <div className="flex items-center gap-2">
                                        <p className="text-sm font-medium text-gray-900 truncate">
                                          {latest.name}
                                        </p>
                                        {/* Hide version badges for NDT and Coating */}
                                        {!isNDTOrCoating && latest.versionNumber && (
                                          <span className="text-xs px-2 py-0.5 text-blueprint-blue bg-[rgba(0,39,63,0.06)] rounded-full font-medium">
                                            v{latest.versionNumber}
                                          </span>
                                        )}
                                        {!isNDTOrCoating && latest.isLatest && (
                                          <span className="text-xs px-2 py-0.5 text-galvanizing-green bg-[rgba(31,154,58,0.08)] rounded-full font-medium">
                                            Latest
                                          </span>
                                        )}
                                      </div>
                                      <div className="flex items-center gap-4 text-xs text-gray-500 mt-1">
                                        <span className="capitalize">{formatDate(latest?.uploadDate || '')}</span>
                                        <span className="capitalize">{latest?.uploadedBy || ''}</span>
                                      </div>
                                    </div>
                                  </div>

                                  <div className="flex items-center gap-3 flex-shrink-0">
                                    {/* Expand/Collapse Button for multiple versions - Hide for NDT and Coating */}
                                    {!isNDTOrCoating && hasMultipleVersions && (
                                      <Button
                                        variant="outline"
                                        size="sm"
                                        onClick={() => toggleGroupExpansion(category)}
                                        className="flex items-center gap-1"
                                      >
                                        {isExpanded ? (
                                          <>
                                            <ChevronUp className="h-4 w-4" />
                                            Hide Versions
                                          </>
                                        ) : (
                                          <>
                                            <ChevronDown className="h-4 w-4" />
                                            Show Versions ({older.length})
                                          </>
                                        )}
                                      </Button>
                                    )}

                                    {/* Download Button */}
                                    <Button
                                      variant="outline"
                                      size="sm"
                                      onClick={() => handleDownload(latest)}
                                      className="flex items-center gap-2"
                                      disabled={!latest.fileUrl}
                                    >
                                      <Download className="h-4 w-4" />
                                      Download
                                    </Button>

                                    {/* Approve/Pending Toggle Button - Only for latest version */}
                                    {latest.isLatest && isVissibleApprovalButton && (
                                      <Button
                                        variant="outline"
                                        size="sm"
                                        title="Click to Approve report"
                                        onClick={() => handleStatusToggle(latest)}
                                        className={`flex items-center gap-1 ${(documentStatuses[latest.id]) === 'approved'
                                          ? 'bg-green-600 hover:bg-green-700 text-white border-green-600'
                                          : 'bg-gray-100 hover:bg-gray-200 text-gray-700 border-gray-300'
                                          }`}
                                      >
                                        {(documentStatuses[latest.id]) === 'approved' ? (
                                          <>
                                            <CheckCircle className="h-4 w-4" />
                                            Approved
                                          </>
                                        ) : (
                                          <>
                                            <FileCheck className="h-4 w-4" />
                                            Approve
                                          </>
                                        )}
                                      </Button>
                                    )}
                                  </div>
                                </div>

                                {/* Older Versions - Only shown when expanded */}
                                {hasMultipleVersions && isExpanded && (
                                  <div className="ml-4 space-y-2 border-l-2 border-gray-200 pl-4">
                                    {older.map((doc) => (
                                      <div
                                        key={doc.id}
                                        className="flex items-center justify-between p-4 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors border border-gray-200"
                                      >
                                        <div className="flex items-center gap-3 flex-1 min-w-0">
                                          <FileText className="h-5 w-5 text-gray-400 flex-shrink-0" />
                                          <div className="flex-1 min-w-0">
                                            <div className="flex items-center gap-2">
                                              <p className="text-sm font-medium text-gray-700 truncate">
                                                {doc.name}
                                              </p>
                                              {doc.versionNumber && (
                                                <span className="text-xs px-2 py-0.5 bg-gray-200 text-gray-600 rounded-full font-medium">
                                                  v{doc.versionNumber}
                                                </span>
                                              )}
                                            </div>
                                            <div className="flex items-center gap-4 text-xs text-gray-500 mt-1">
                                              <span className="capitalize">{formatDate(doc?.uploadDate || '')}</span>
                                              <span className="capitalize">{doc?.uploadedBy || ''}</span>
                                            </div>
                                          </div>
                                        </div>

                                        <div className="flex items-center gap-3 flex-shrink-0">
                                          {/* Download Button Only - No Approve Button for older versions */}
                                          <Button
                                            variant="outline"
                                            size="sm"
                                            title="Click to Download report"
                                            onClick={() => handleDownload(doc)}
                                            className="flex items-center gap-2"
                                            disabled={!doc.fileUrl}
                                          >
                                            <Download className="h-4 w-4" />
                                            Download
                                          </Button>
                                        </div>
                                      </div>
                                    ))}
                                  </div>
                                )}
                              </div>
                            )
                          )}

                          {/* If no latest version but has older versions (edge case) - Only for non-NDT/Coating */}
                          {!isNDTOrCoating && !latest && older.length > 0 && (
                            <div className="space-y-2">
                              {older.map((doc) => (
                                <div
                                  key={doc.id}
                                  className="flex items-center justify-between p-4 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors border border-gray-200"
                                >
                                  <div className="flex items-center gap-3 flex-1 min-w-0">
                                    <FileText className="h-5 w-5 text-gray-500 flex-shrink-0" />
                                    <div className="flex-1 min-w-0">
                                      <div className="flex items-center gap-2">
                                        <p className="text-sm font-medium text-gray-900 truncate">
                                          {doc.name}
                                        </p>
                                        {doc.versionNumber && (
                                          <span className="text-xs px-2 py-0.5 bg-gray-200 text-gray-600 rounded-full font-medium">
                                            v{doc.versionNumber}
                                          </span>
                                        )}
                                      </div>
                                      <div className="flex items-center gap-4 text-xs text-gray-500 mt-1">
                                        <span className="capitalize">{formatDate(doc?.uploadDate || '')}</span>
                                        <span className="capitalize">{doc?.uploadedBy || ''}</span>
                                      </div>
                                    </div>
                                  </div>

                                  <div className="flex items-center gap-3 flex-shrink-0">
                                    <Button
                                      variant="outline"
                                      size="sm"
                                      title="Click to Download report"
                                      onClick={() => handleDownload(doc)}
                                      className="flex items-center gap-2"
                                      disabled={!doc.fileUrl}
                                    >
                                      <Download className="h-4 w-4" />
                                      Download
                                    </Button>
                                  </div>
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              
            </div>

            {/* Summary Section */}
            <div className="border-t pt-4 mt-4">
              <div className="flex items-center justify-between">
                {/* <div className="flex items-center gap-6">
              <div className="flex items-center gap-2">
                <div className="flex items-center gap-2 px-3 py-1.5 bg-green-50 rounded-md border">
                  <CheckCircle className="h-4 w-4 text-green-600" />
                  <span className="text-sm font-medium text-green-700">
                    Approved: <span className="font-bold">{statusCounts.approved}</span>
                  </span>
                </div>
              </div>

             <div className="flex items-center gap-2">
                <div className="flex items-center gap-2 px-3 py-1.5 bg-gray-50 rounded-md border border-gray-200">
                  <FileText className="h-4 w-4 text-gray-600" />
                  <span className="text-sm font-medium text-gray-700">
                    Pending: <span className="font-bold">{statusCounts.pending}</span>
                  </span>
                </div>
              </div> 
            </div> */}
                <div className="text-sm text-gray-500">
                  Total: <span className="font-semibold text-gray-700">{statusCounts.total}</span> versions
                </div>
              </div>
            </div>
          </>
        }
        <DialogFooter>
          <Button
            variant="outline"
            onClick={onClose}
            className="flex items-center gap-2"
          >
           Cancel
          </Button>
          {isVissibleApprovalButton && (
            <Button
            onClick={handleSubmit}
            title="Submit Approval"
            disabled={isSubmitting || !hasChanges}
            className="flex items-center gap-2"
          >
            {isSubmitting ? (
              <>
                <Loader2 className="h-4 w-4 animate-spin" />
                Submitting...
              </>
            ) : (
              'Submit Approval'
            )}
          </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

